/**
 * Tabs — composant onglets controlled, accessibles ARIA.
 *
 *   <Tabs
 *     value={tab}
 *     onChange={setTab}
 *     items={[
 *       { value: 'overview', label: 'Aperçu' },
 *       { value: 'content', label: 'Contenu généré' },
 *       { value: 'mockups', label: 'Mockups', disabled: true },
 *     ]}
 *   />
 *
 * Reste un composant sans contenu — l'appelant rend le panneau actif lui-meme
 * (`{tab === 'overview' && <OverviewPanel />}`). Ca evite de coupler la
 * decision de mounting/lazy loading au composant Tabs.
 */

import type { KeyboardEvent } from 'react'
import { useCallback } from 'react'

export interface TabItem<TValue extends string = string> {
  value: TValue
  label: string
  disabled?: boolean
  /** Petit indicateur a droite (ex: count, badge "à venir"). */
  trailing?: React.ReactNode
}

interface TabsProps<TValue extends string> {
  value: TValue
  onChange: (next: TValue) => void
  items: TabItem<TValue>[]
  ariaLabel?: string
  className?: string
}

export default function Tabs<TValue extends string = string>({
  value,
  onChange,
  items,
  ariaLabel = 'Onglets',
  className,
}: TabsProps<TValue>) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, idx: number) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      e.preventDefault()
      const direction = e.key === 'ArrowRight' ? 1 : -1
      // On saute les `disabled` lors de la navigation clavier.
      let next = idx
      for (let i = 0; i < items.length; i += 1) {
        next = (next + direction + items.length) % items.length
        if (!items[next].disabled) break
      }
      const target = items[next]
      if (target && !target.disabled) onChange(target.value)
    },
    [items, onChange],
  )

  const classes = ['ui-tabs', className].filter(Boolean).join(' ')

  return (
    <div role="tablist" aria-label={ariaLabel} className={classes}>
      {items.map((item, idx) => {
        const active = item.value === value
        const tabClasses = ['ui-tab', active && 'ui-tab--active'].filter(Boolean).join(' ')
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            aria-disabled={item.disabled}
            tabIndex={active ? 0 : -1}
            disabled={item.disabled}
            className={tabClasses}
            onClick={() => !item.disabled && onChange(item.value)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
          >
            <span>{item.label}</span>
            {item.trailing}
          </button>
        )
      })}
    </div>
  )
}
